import { useEffect } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

// helpers
import './i18n';

//components
import Login from 'modules/auth/pages/login/login';
import DashboardLayout from 'core/layout/layout';
import ListRolesPage from 'modules/roles/pages/list/list';
import UpdateRolesPage from 'modules/roles/pages/update/update';
import CreateRolesPage from 'modules/roles/pages/create/create';
import ListAdminsPage from 'modules/admins/pages/list/list';
import AdminChangePasswordPage from 'modules/admins/pages/changePassword/changePassword';
import NotFoundPage from 'components/404/notFound';
import UpdateAdminPage from 'modules/admins/pages/update/update';
import CreateAdminPage from 'modules/admins/pages/create/create';
import ProfilePage from 'modules/profile/pages/profile/profile';
import ProfileDataComponent from 'modules/profile/components/data/data';
import ProfileBillingComponent from 'modules/profile/components/billing/billing';
import ProfileDataEditComponent from 'modules/profile/components/data-edit/data-edit';
import ProfileChangePasswordComponent from 'modules/profile/components/data-change-password/data-change-password';
import DashboardPage from 'modules/dashboard/pages/dashboard/dashboard';
import OrdersPage from 'modules/orders/pages/ordersPage';
import ListUserPage from 'modules/users/pages/list/list';
import AuthGuard from 'core/guards/authGuard';
import ListCountriesPage from 'modules/countries/pages/list/list';
import UpdateCountryPage from 'modules/countries/pages/update/update';
import CreateCountryPage from 'modules/countries/pages/create/create';
import SplashScreenCreatePage from 'modules/splashScreen/pages/create/create';
import ListRegionsPage from 'modules/regions/pages/list/list';
import CreateRegionPage from 'modules/regions/pages/create/create';
import UpdateRegionPage from 'modules/regions/pages/update/update';
import ListCitiesPage from 'modules/cities/pages/list/list';
import CreateCityPage from 'modules/cities/pages/create/create';
import UpdateCityPage from 'modules/cities/pages/update/update';
import ListDistrictsPage from 'modules/districts/pages/list/list';
import CreateDistrictPage from 'modules/districts/pages/create/create';
import UpdateDistrictPage from 'modules/districts/pages/update/update';
import ListCurrenciesPage from 'modules/currencies/pages/list/list';
import CreateCurrencyPage from 'modules/currencies/pages/create/create';
import UpdateCurrencyPage from 'modules/currencies/pages/update/update';
import ListLanguagesPage from 'modules/languages/pages/list/list';
import CreateLanguagePage from 'modules/languages/pages/create/create';
import UpdateLanguagePage from 'modules/languages/pages/update/update';
import NotAuthorizedPage from 'components/notAuthorized/notAuthorized';
import ListBanksPage from 'modules/banks/pages/list/list';
import CreateBankPage from 'modules/banks/pages/create/create';
import UpdateBankPage from 'modules/banks/pages/update/update';
import ListSettingCategoriesPage from 'modules/settings/pages/list/list';
import CreateUserPage from 'modules/users/pages/create/create';
import UpdateUserPage from 'modules/users/pages/update/update';
import UserChangePasswordPage from 'modules/users/pages/changePassword/changePassword';
import ViewUserPage from 'modules/users/pages/view/view';
import ListSubscriptionsPage from 'modules/subscription/pages/list/list';

// services
import { axiosRequestInterceptor } from 'core/services/interceptor';

// styles
import './App.scss';
import './assets/styles/customAntComponents.scss';
import { getDefaultLanguage } from 'core/services/language';
import ListMinorsPage from 'modules/minors/pages/list/list';
import MinorViewPage from 'modules/minors/pages/view/view';
import ListIncompetentsPage from 'modules/incompetents/pages/list/list';
import IncompetentViewPage from 'modules/incompetents/pages/view/view';
import ListCommercialEntitiesPage from 'modules/commercialEntities/pages/list/list';
import CommercialEntityViewPage from 'modules/commercialEntities/pages/view/view';

const router = createBrowserRouter([
	{
		path: '/login',
		Component: Login,
	},
	{
		path: '/',
		element: (
			<AuthGuard>
				<DashboardLayout />
			</AuthGuard>
		),
		children: [
			// { path: 'splash-screens', Component: SplashScreenListPage },
			{ path: 'splash-screens/create', Component: SplashScreenCreatePage },

			{ path: 'roles', Component: ListRolesPage },
			{ path: 'roles/create', Component: CreateRolesPage },
			{ path: 'roles/:id', Component: UpdateRolesPage },

			{ path: 'admins', Component: ListAdminsPage },
			{ path: 'admins/create', Component: CreateAdminPage },
			{ path: 'admins/:id', Component: UpdateAdminPage },
			{ path: 'admins/:id/change-password', Component: AdminChangePasswordPage },

			{ path: 'dashboard', Component: DashboardPage },

			{ path: 'orders', Component: OrdersPage },

			{ path: 'countries', Component: ListCountriesPage },
			{ path: 'countries/create', Component: CreateCountryPage },
			{ path: 'countries/:id', Component: UpdateCountryPage },

			{ path: 'regions', Component: ListRegionsPage },
			{ path: 'regions/create', Component: CreateRegionPage },
			{ path: 'regions/:id', Component: UpdateRegionPage },

			{ path: 'cities', Component: ListCitiesPage },
			{ path: 'cities/create', Component: CreateCityPage },
			{ path: 'cities/:id', Component: UpdateCityPage },

			{ path: 'districts', Component: ListDistrictsPage },
			{ path: 'districts/create', Component: CreateDistrictPage },
			{ path: 'districts/:id', Component: UpdateDistrictPage },

			{ path: 'currencies', Component: ListCurrenciesPage },
			{ path: 'currencies/create', Component: CreateCurrencyPage },
			{ path: 'currencies/:id', Component: UpdateCurrencyPage },

			{ path: 'languages', Component: ListLanguagesPage },
			{ path: 'languages/create', Component: CreateLanguagePage },
			{ path: 'languages/:id', Component: UpdateLanguagePage },

			{ path: 'banks', Component: ListBanksPage },
			{ path: 'banks/create', Component: CreateBankPage },
			{ path: 'banks/:id', Component: UpdateBankPage },

			{ path: 'users', Component: ListUserPage },
			{ path: 'users/create', Component: CreateUserPage },
			{ path: 'users/edit/:id', Component: UpdateUserPage },
			{ path: 'users/view/:id', Component: ViewUserPage },
			{ path: 'users/change-password/:id', Component: UserChangePasswordPage },

			{ path: 'settings', Component: ListSettingCategoriesPage },

			{ path: 'subscriptions', Component: ListSubscriptionsPage },

			{
				path: 'profile',
				Component: ProfilePage,
				children: [
					{ path: 'data', Component: ProfileDataComponent },
					{ path: 'data/edit', Component: ProfileDataEditComponent },
					{ path: 'data/change-password', Component: ProfileChangePasswordComponent },
					{ path: 'billing', Component: ProfileBillingComponent },
				],
			},

			{ path: 'minors', Component: ListMinorsPage },
			{ path: 'minors/:id', Component: MinorViewPage },

			{ path: 'incompetents', Component: ListIncompetentsPage },
			{ path: 'incompetents/:id', Component: IncompetentViewPage },

			{ path: 'commercial-entities', Component: ListCommercialEntitiesPage },
			{ path: 'commercial-entities/:id', Component: CommercialEntityViewPage },

			{ path: 'not-authorized', Component: NotAuthorizedPage },
		],
	},
	{
		path: '*',
		Component: NotFoundPage,
	},
]);

function App() {
	axiosRequestInterceptor();

	// check for previous language on first load
	useEffect(() => {
		document.body.dir = getDefaultLanguage() == 'ar' ? 'rtl' : 'ltr';
	}, []);

	return <RouterProvider router={router} />;
}

export default App;
